<template>
  <!-- How long an appliance still has, drawn: a ring for the share of the cycle
       that is left, and the same reading in words beside it. Purely a picture of
       the `runtime` it is handed — the card that owns the device's live values
       decides what that runtime is, so a card keeps exactly one subscription. -->
  <div v-if="runtime" class="appliance-runtime" :title="titleText">
    <AutomationProgressRing
      :ratio="runtime.ratio"
      :size="ringSize"
      :stroke-width="runtime.ratio === null ? 3 : 4"
      :color="color"
      icon="timer" />
    <span v-if="!compact" class="appliance-runtime__label">{{ runtime.label }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import AutomationProgressRing from './AutomationProgressRing.vue';
import { tt } from '../../utils/consoleText';
import type { ApplianceRuntime } from '../../utils/applianceRuntime';

const props = withDefaults(
  defineProps<{
    /** Nothing is drawn when there is no cycle to count: an idle machine has no
     *  remaining time, and a "0" would read as a countdown that has finished. */
    runtime?: ApplianceRuntime | null;
    /** A `min` card is a picture and a name: the ring alone, no label. */
    compact?: boolean;
    /** The device's own colour while it is live. */
    accent?: string | null;
  }>(),
  { runtime: null, compact: false, accent: null },
);

const ringSize = computed(() => (props.compact ? 20 : 22));
const color = computed(() => props.accent || 'rgb(16, 185, 129)');

const titleText = computed(() => {
  if (!props.runtime) return '';
  const source =
    props.runtime.source === 'program'
      ? tt('Cycle', 'Program')
      : props.runtime.source === 'timer'
        ? tt('Switch-off timer', 'Časovnik izklopa')
        : tt('Run limit', 'Omejitev delovanja');
  return `${source}: ${props.runtime.label} ${tt('left', 'še')}`;
});
</script>

<style scoped>
.appliance-runtime {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1rem 0.3rem 0.1rem 0.1rem;
  border-radius: 9999px;
  background: color-mix(in srgb, var(--card-base, #fff) 72%, transparent);
  backdrop-filter: blur(4px);
  color: rgb(71 85 105);
}

.dark .appliance-runtime {
  color: rgb(226 232 240);
  background: color-mix(in srgb, rgb(15 23 42) 55%, transparent);
}

.appliance-runtime__label {
  font-size: 0.7rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.01em;
  white-space: nowrap;
}
</style>
